import { HomeCareLeadId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { handleEvent } from "util/user-events";
import { AiSummaryState } from "./ai-summary-state";
import { useEntityAiSummary } from "./use-entity-ai-summary";

export const useHomeCareLeadAiSummary = (
    homeCareLeadId: HomeCareLeadId | null,
): AiSummaryState => {
    const trpcClient = trpc.useTRPC();

    const summaryQuery = useQuery({
        ...trpcClient.homeCareLeadAiSummary.find.queryOptions({
            entityId: homeCareLeadId!,
        }),
        enabled: homeCareLeadId !== null,
    });
    const generateMutation = useMutation({
        ...trpcClient.homeCareLeadAiSummary.generate.mutationOptions(),
    });
    const submitFeedbackMutation = useMutation({
        ...trpcClient.homeCareLeadAiSummary.submitFeedback.mutationOptions(),
    });
    const updateTextMutation = useMutation({
        ...trpcClient.homeCareLeadAiSummary.updateText.mutationOptions(),
    });
    const removeSummaryMutation = useMutation({
        ...trpcClient.homeCareLeadAiSummary.removeSummary.mutationOptions(),
    });

    return useEntityAiSummary({
        summary: summaryQuery.data,
        isQueryLoading: summaryQuery.isLoading,
        queryError: summaryQuery.error?.message,
        refetchSummary: () => summaryQuery.refetch(),

        triggerGenerate: () => {
            if (!homeCareLeadId) {
                return Promise.reject(
                    new Error(
                        "Cannot generate a home care lead summary without a home care lead id",
                    ),
                );
            }
            return generateMutation.mutateAsync({ entityId: homeCareLeadId });
        },
        isGenerating: generateMutation.isPending,
        generateError: generateMutation.error?.message,

        triggerSubmitFeedback: input => submitFeedbackMutation.mutate(input),
        isSubmittingFeedback: submitFeedbackMutation.isPending,
        submitFeedbackError: submitFeedbackMutation.error?.message,

        triggerUpdateText: input => updateTextMutation.mutateAsync(input),
        updateTextError: updateTextMutation.error?.message,

        triggerRemoveSummary: input => removeSummaryMutation.mutateAsync(input),
        isRemoving: removeSummaryMutation.isPending,
        removeSummaryError: removeSummaryMutation.error?.message,

        trackGenerate: () =>
            handleEvent("ai-summary-generate", { homeCareLeadId, isRegenerate: false }),
        trackRegenerate: () =>
            handleEvent("ai-summary-generate", { homeCareLeadId, isRegenerate: true }),
    });
};
